import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { Permission } from '@ssn/shared';
import { useSession } from './session/SessionProvider';
import { AppShell } from './layout/AppShell';
import { LoadingBlock } from './components/ui/Feedback';
import { SignInPage } from './pages/SignInPage';
import { NoAccessPage } from './pages/NoAccessPage';
import { DashboardPage } from './pages/DashboardPage';
import { TaskListPage } from './pages/TaskListPage';
import { CreateTaskPage } from './pages/CreateTaskPage';
import { TaskDetailPage } from './pages/TaskDetailPage';
import { ReviewQueuePage } from './pages/ReviewQueuePage';
import { TeamPage } from './pages/TeamPage';
import { TicketListPage as TicketQueuePage } from './pages/TicketListPage';
import { TicketDetailPage } from './pages/TicketDetailPage';

/*
 * The oversight and administration screens are split out of the initial bundle.
 * A developer opening the dashboard should not pay to download the analytics
 * charts and the audit table they may never look at.
 */
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const AuditPage = lazy(() => import('./pages/AuditPage').then((m) => ({ default: m.AuditPage })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const DeveloperProfilePage = lazy(() =>
  import('./pages/DeveloperProfilePage').then((m) => ({ default: m.DeveloperProfilePage })),
);
import { NotFoundPage } from './pages/NotFoundPage';
import { ForbiddenPage } from './pages/ForbiddenPage';

export function App() {
  const { status } = useSession();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div className="grid min-h-dvh place-items-center bg-canvas">
        <LoadingBlock label="Signing you in" />
      </div>
    );
  }

  if (status === 'denied') {
    return (
      <Routes>
        <Route path="*" element={<NoAccessPage />} />
      </Routes>
    );
  }

  if (status === 'anonymous') {
    return (
      <Routes>
        <Route path="/no-access" element={<NoAccessPage />} />
        <Route path="/signin" element={<SignInPage />} />
        <Route
          path="*"
          element={<Navigate to={`/signin?redirect=${encodeURIComponent(location.pathname)}`} replace />}
        />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Already signed in — the sign-in screen has nothing to offer. */}
      <Route path="/signin" element={<Navigate to="/" replace />} />
      <Route path="/no-access" element={<Navigate to="/" replace />} />

      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />

        <Route
          path="tasks"
          element={
            <Guard permission="task:read:team">
              <TaskListPage scope="ALL" />
            </Guard>
          }
        />
        <Route
          path="tasks/mine"
          element={
            <Guard permission="task:read:own">
              <TaskListPage scope="MINE" />
            </Guard>
          }
        />
        <Route
          path="tasks/new"
          element={
            <Guard permission="task:create">
              <CreateTaskPage />
            </Guard>
          }
        />
        <Route path="tasks/:reference" element={<TaskDetailPage />} />

        <Route
          path="tickets"
          element={
            <Guard permission={['ticket:read:own', 'ticket:read:team', 'ticket:read:all']}>
              <TicketQueuePage />
            </Guard>
          }
        />
        <Route
          path="tickets/:reference"
          element={
            <Guard permission={['ticket:read:own', 'ticket:read:team', 'ticket:read:all']}>
              <TicketDetailPage />
            </Guard>
          }
        />

        <Route
          path="review"
          element={
            <Guard permission="review:queue:read">
              <ReviewQueuePage />
            </Guard>
          }
        />
        {/* The review view of a task is the task detail, opened in review mode. */}
        <Route
          path="review/:reference"
          element={
            <Guard permission="review:queue:read">
              <TaskDetailPage reviewMode />
            </Guard>
          }
        />

        <Route
          path="team"
          element={
            <Guard permission="user:read">
              <TeamPage />
            </Guard>
          }
        />
        <Route
          path="team/:userId"
          element={
            <Suspense fallback={<LoadingBlock />}>
              <DeveloperProfilePage />
            </Suspense>
          }
        />

        <Route
          path="analytics"
          element={
            <Guard permission="analytics:read:team">
              <AnalyticsPage />
            </Guard>
          }
        />
        <Route
          path="audit"
          element={
            <Guard permission="audit:read">
              <AuditPage />
            </Guard>
          }
        />
        <Route
          path="settings"
          element={
            <Guard permission="settings:read">
              <SettingsPage />
            </Guard>
          }
        />

        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

/**
 * Hides a route the viewer has no business seeing.
 *
 * This is navigation hygiene, not security: the API refuses the underlying
 * request regardless of what the browser renders (§21, §26).
 */
function Guard({
  permission,
  children,
}: {
  permission: Permission | readonly Permission[];
  children: React.ReactNode;
}) {
  const { can, canAny } = useSession();
  const allowed = Array.isArray(permission) ? canAny(...permission) : can(permission as Permission);
  if (!allowed) return <ForbiddenPage />;
  return <Suspense fallback={<LoadingBlock />}>{children}</Suspense>;
}
